import { useMemo, useState } from 'react';
import { useUser } from '../contexts/UserContext';

function downloadJson(filename, data) {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export default function AccountSettings({ embedded = false }) {
  const {
    currentUser,
    updateProfile,
    exportMyData,
    requestAccountDeletion,
    resendEmailVerification,
  } = useUser();

  const [form, setForm] = useState(() => ({
    preferredLanguage: currentUser?.preferredLanguage || 'fr',
    emailNotifications:
      currentUser?.notificationPreferences?.email !== false,
    targetedCompetitions: (currentUser?.targetedCompetitions || []).join(', '),
    prioritySubjects: (currentUser?.prioritySubjects || []).join(', '),
    preparationObjective: currentUser?.preparationObjective || '',
  }));
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState('');

  const verified = Boolean(currentUser?.emailVerifiedAt);

  const consent = useMemo(
    () => ({
      terms: currentUser?.termsAcceptedAt
        ? new Date(currentUser.termsAcceptedAt).toLocaleDateString('fr-FR') +
          ' · ' +
          (currentUser.termsVersion || 'version non renseignée')
        : 'Non enregistré',
      privacy: currentUser?.privacyAcceptedAt
        ? new Date(currentUser.privacyAcceptedAt).toLocaleDateString('fr-FR') +
          ' · ' +
          (currentUser.privacyVersion || 'version non renseignée')
        : 'Non enregistré',
    }),
    [currentUser]
  );

  async function save(event) {
    event.preventDefault();
    try {
      setBusy('save');
      setMessage('');
      await updateProfile({
        preferredLanguage: form.preferredLanguage,
        notificationPreferences: {
          ...(currentUser?.notificationPreferences || {}),
          email: form.emailNotifications,
        },
        targetedCompetitions: form.targetedCompetitions
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean),
        prioritySubjects: form.prioritySubjects
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean),
        preparationObjective: form.preparationObjective || null,
      });
      setMessage('Préférences enregistrées.');
    } catch (error) {
      setMessage(error.message || 'Mise à jour impossible.');
    } finally {
      setBusy('');
    }
  }

  async function verifyEmail() {
    try {
      setBusy('verify');
      setMessage('');
      const result = await resendEmailVerification();
      setMessage(
        result?.alreadyVerified
          ? 'Votre adresse e-mail est déjà vérifiée.'
          : 'Un nouveau lien de vérification a été envoyé.'
      );
    } catch (error) {
      setMessage(error.message || 'Envoi impossible.');
    } finally {
      setBusy('');
    }
  }

  async function exportData() {
    try {
      setBusy('export');
      setMessage('');
      const data = await exportMyData();
      downloadJson(
        'concoursbf-mes-donnees-' + (currentUser?.id || 'compte') + '.json',
        data
      );
      setMessage('Export généré.');
    } catch (error) {
      setMessage(error.message || 'Export impossible.');
    } finally {
      setBusy('');
    }
  }

  async function requestDeletion() {
    const reason = window.prompt(
      'Vous pouvez indiquer un motif (facultatif). La demande vous déconnectera immédiatement :'
    );
    if (reason === null) return;
    if (
      !window.confirm(
        'Confirmer la demande de suppression du compte ? Vous serez déconnecté immédiatement.'
      )
    ) {
      return;
    }

    try {
      setBusy('delete');
      await requestAccountDeletion(reason.trim());
    } catch (error) {
      setMessage(error.message || 'Demande de suppression impossible.');
      setBusy('');
    }
  }

  return (
    <div className={embedded
      ? 'cb-account-settings-embedded'
      : 'surface-card p-6 rounded-2xl shadow-sm border border-color transition-all'}>
      {!embedded && <h2 className="text-xl font-bold mb-6">⚙️ Préférences & compte</h2>}

      {message && (
        <div className="mb-4 p-3 rounded-lg bg-secondary text-sm">
          {message}
        </div>
      )}

      <form className="space-y-4" onSubmit={save}>
        <label className="block">
          <span className="text-sm font-semibold text-secondary">
            Langue préférée
          </span>
          <select
            className="mt-1 w-full px-3 py-2 border border-color rounded-lg bg-primary"
            value={form.preferredLanguage}
            onChange={(event) =>
              setForm((previous) => ({
                ...previous,
                preferredLanguage: event.target.value,
              }))
            }
          >
            <option value="fr">Français</option>
            <option value="en">English</option>
          </select>
        </label>

        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={form.emailNotifications}
            onChange={(event) =>
              setForm((previous) => ({
                ...previous,
                emailNotifications: event.target.checked,
              }))
            }
          />
          <span className="text-sm">
            Recevoir les notifications utiles par e-mail
          </span>
        </label>

        <p className="cb-account-settings-personalization-note text-xs text-secondary">
          Ces choix sont enregistrés dans votre profil. Les recommandations automatiques
          selon vos concours et matières ne sont pas encore activées.
        </p>

        <label className="block">
          <span className="text-sm font-semibold text-secondary">
            Concours ciblés
          </span>
          <input
            className="mt-1 w-full px-3 py-2 border border-color rounded-lg bg-primary"
            placeholder="Ex. ENAREF, Douanes, ENSP"
            value={form.targetedCompetitions}
            onChange={(event) =>
              setForm((previous) => ({
                ...previous,
                targetedCompetitions: event.target.value,
              }))
            }
          />
        </label>

        <label className="block">
          <span className="text-sm font-semibold text-secondary">
            Matières prioritaires
          </span>
          <input
            className="mt-1 w-full px-3 py-2 border border-color rounded-lg bg-primary"
            placeholder="Ex. Mathématiques, Français"
            value={form.prioritySubjects}
            onChange={(event) =>
              setForm((previous) => ({
                ...previous,
                prioritySubjects: event.target.value,
              }))
            }
          />
        </label>

        <label className="block">
          <span className="text-sm font-semibold text-secondary">
            Objectif de préparation
          </span>
          <textarea
            rows="3"
            className="mt-1 w-full px-3 py-2 border border-color rounded-lg bg-primary"
            value={form.preparationObjective}
            onChange={(event) =>
              setForm((previous) => ({
                ...previous,
                preparationObjective: event.target.value,
              }))
            }
          />
        </label>

        <button
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg disabled:opacity-50"
          disabled={Boolean(busy)}
        >
          {busy === 'save' ? 'Enregistrement…' : 'Enregistrer les préférences'}
        </button>
      </form>

      <div className="mt-6 pt-5 border-t border-color space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="font-semibold">Adresse e-mail</p>
            <p className="text-sm text-secondary">
              {verified ? 'Vérifiée' : 'Non vérifiée'}
            </p>
          </div>
          {!verified && (
            <button
              type="button"
              className="px-3 py-2 rounded-lg bg-secondary font-medium disabled:opacity-50"
              onClick={verifyEmail}
              disabled={Boolean(busy)}
            >
              {busy === 'verify' ? 'Envoi…' : 'Renvoyer la vérification'}
            </button>
          )}
        </div>

        <div className="text-xs text-secondary space-y-1">
          <p>Conditions d’utilisation : {consent.terms}</p>
          <p>Politique de confidentialité : {consent.privacy}</p>
        </div>

        <button
          type="button"
          className="w-full text-left px-4 py-3 bg-secondary rounded-xl font-medium disabled:opacity-50"
          onClick={exportData}
          disabled={Boolean(busy)}
        >
          {busy === 'export'
            ? 'Préparation de l’export…'
            : '📦 Exporter mes données'}
        </button>

        <button
          type="button"
          className="w-full text-left px-4 py-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded-xl font-medium disabled:opacity-50"
          onClick={requestDeletion}
          disabled={Boolean(busy)}
        >
          {busy === 'delete'
            ? 'Traitement…'
            : '🗑️ Demander la suppression de mon compte'}
        </button>
      </div>
    </div>
  );
}
